import React, { useState, useCallback, useRef } from 'react';
import { Volume2, VolumeX, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Audio cache to avoid re-fetching
const audioCache = new Map();

const AudioButton = ({ 
  text, 
  language = 'en', // 'en', 'ja', 'pt'
  className,
  size = 'default', // 'sm', 'default', 'lg'
  useTTS = true // Use OpenAI TTS API (false = browser speech synthesis)
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const audioRef = useRef(null);

  const sizeClasses = {
    sm: 'w-8 h-8',
    default: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    default: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  // Detect language from text if not specified
  const detectLanguage = (text) => {
    // Check for Japanese characters
    if (/[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/.test(text)) {
      return 'ja';
    }
    // Check for Portuguese accents
    if (/[àáâãäéêíóôõúç]/i.test(text)) {
      return 'pt';
    }
    return 'en';
  };

  const playWithTTS = useCallback(async () => {
    if (!text || isPlaying || isLoading) return;

    const effectiveLanguage = language === 'auto' ? detectLanguage(text) : language;
    const cacheKey = `${text}:${effectiveLanguage}`;

    setIsLoading(true);
    setError(false);

    try {
      let audioBlob;

      // Check cache first
      if (audioCache.has(cacheKey)) {
        audioBlob = audioCache.get(cacheKey);
      } else {
        // Fetch from TTS API
        const params = new URLSearchParams({
          text: text,
          language: effectiveLanguage,
          voice: 'auto',
          speed: '1.0'
        });

        const response = await fetch(`${API_URL}/api/tts/generate?${params}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('TTS request failed');
        }

        audioBlob = await response.blob();
        
        // Cache the audio (limit cache size)
        if (audioCache.size < 100) {
          audioCache.set(cacheKey, audioBlob);
        }
      }

      // Create audio element and play
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onplay = () => {
        setIsPlaying(true);
        setIsLoading(false);
      };

      audio.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setIsPlaying(false);
        setIsLoading(false);
        setError(true);
        // Fallback to browser TTS
        fallbackToSpeechSynthesis();
      };

      await audio.play();

    } catch (err) {
      console.error('TTS error:', err);
      setIsLoading(false);
      // Fallback to browser speech synthesis
      fallbackToSpeechSynthesis();
    }
  }, [text, language, isPlaying, isLoading]);

  const fallbackToSpeechSynthesis = useCallback(() => {
    if (!text) return;

    if ('speechSynthesis' in window) {
      setIsPlaying(true);
      setError(false);

      const effectiveLanguage = language === 'auto' ? detectLanguage(text) : language;
      const langCode = {
        'en': 'en-US',
        'ja': 'ja-JP',
        'pt': 'pt-BR'
      }[effectiveLanguage] || 'en-US';

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = langCode;
      utterance.rate = 0.85;
      utterance.pitch = 1;

      utterance.onend = () => setIsPlaying(false);
      utterance.onerror = () => {
        setIsPlaying(false);
        setError(true);
      };

      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    } else {
      setError(true);
    }
  }, [text, language]);

  const speak = useCallback(() => {
    if (useTTS) {
      playWithTTS();
    } else {
      fallbackToSpeechSynthesis();
    }
  }, [useTTS, playWithTTS, fallbackToSpeechSynthesis]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsPlaying(false);
    setIsLoading(false);
  }, []);

  const handleClick = () => {
    if (isPlaying) {
      stopAudio();
    } else {
      speak();
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading || !text}
      className={cn(
        'audio-btn',
        isPlaying && 'animate-pulse-soft bg-primary/20',
        isLoading && 'opacity-70',
        error && 'text-destructive bg-destructive/10',
        sizeClasses[size],
        className
      )}
      title={error ? 'Áudio não disponível' : isPlaying ? 'Parar' : 'Ouvir pronúncia'}
      data-testid="audio-button"
    >
      {isLoading ? (
        <Loader2 className={cn(iconSizes[size], 'animate-spin')} />
      ) : error ? (
        <VolumeX className={iconSizes[size]} />
      ) : (
        <Volume2 className={cn(iconSizes[size], isPlaying && 'text-primary')} />
      )}
    </button>
  );
};

export default AudioButton;
